import { useState, useCallback } from "react";
import { apiFetch } from "../apiFetch";
import HelpTip from "./HelpTip";

// Newest usable Sentinel-2 view of a ~5 km box around one node (issue #109).
//
// The whole point of this panel is the contrast with the basemap underneath it.
// The map's detail imagery is Esri World Imagery, which over this corridor
// resolves 0.5 m and was photographed 2020-02-17 — measured against Esri's own
// metadata service on 2026-09-06 (#82); SATELLITE_IMAGERY.md §1 has the command
// to re-check it. This is 10 m/pixel and days old. Neither is "better":
// sub-metre AND current does not exist free from any source. Showing both with
// their dates is a stronger claim than presenting either as authoritative, and
// it stops an operator reading a six-year-old picture as today's ground.
//
// Two corrections to what this comment used to say, both the same mistake in
// different units. The basemap is not "0.22 m/pixel" — that is the zoom-19 tile
// grid's scale, while the imagery resolves 0.5 m and Esri draws this source only
// to map level 17, so z18 and z19 are magnified rather than sharper. And it is
// not "dated 2021", which was Esri's Wayback publication date rather than when
// the shutter opened.
//
// Four things are therefore never omitted, and every one of them has a failure
// mode behind it:
//
//   capture date      — an undated image is indistinguishable from the basemap
//   cloud cover       — white is read as terrain when it is not labelled cloud
//   metres per pixel  — one pixel is wider than a tower; this is not a photo
//                       of a structure
//   "older scene"     — reaching back past cloudy passes is said out loud,
//                       never done quietly (the #57 and #93 precedent)
//
// Fetching is on demand rather than on panel open: every view costs a
// third-party quota on the first look at a node, and most panel opens are for
// the sensor data, not the imagery.
//
// The button does two different things, which is deliberate (#121). The first
// press asks for the cached answer — cheap, and usually right. Every press
// after that forces a re-render with `?refresh=1`, because the reason to press
// a button labelled Refresh is that what is on screen is wrong, and before this
// it re-requested the same endpoint and got the same cached entry back. A
// forced render costs a Copernicus processing unit, so the API rate-limits it
// per node and the wait is shown as a wait, not as a failure.

// Cloud percentage above which the badge turns amber. Below this the scene is
// usable as a picture of the ground; above it, enough of the box may be cloud
// that the reader should be looking at the number, not the image.
const CLOUD_WARN_PCT = 10;

export default function NodeSatelliteSnapshot({
  nodeId,
  apiBaseUrl,
  t = (k) => k,
  simulated = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cooldown, setCooldown] = useState(null);

  const load = useCallback(
    async ({ refresh = false } = {}) => {
      setLoading(true);
      setError(null);
      setCooldown(null);
      try {
        const url =
          `${apiBaseUrl}/api/node-image?node_id=${encodeURIComponent(nodeId)}` +
          (refresh ? "&refresh=1" : "");
        const res = await apiFetch(url);

        // Refused because this node was refreshed a moment ago. Nothing is
        // broken and nothing is lost — the image already on screen stays, and
        // the panel says when it can be asked for again.
        if (res.status === 429) {
          const body = await res.json().catch(() => ({}));
          const wait = Number(body.retry_after_s);
          setCooldown(Number.isFinite(wait) && wait > 0 ? Math.ceil(wait) : 1);
          return;
        }

        if (!res.ok) throw new Error(`API error ${res.status}`);
        setData(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [nodeId, apiBaseUrl]
  );

  // Every tier below superuser sees simulated data everywhere else on this
  // panel, and there is nothing to simulate here — a fake satellite image would
  // be a fabricated observation of a real place. The button is not offered.
  if (simulated) return null;

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <span style={styles.title}>
          {t("satSnapshotTitle")}
          {/* Never `simulated`: this panel returns null below superuser rather
              than fabricating an observation of a real place. */}
          <HelpTip topic="satelliteSnapshot" t={t} />
        </span>
        <button
          type="button"
          // Only a re-press forces a render. The first press has nothing on
          // screen to disbelieve, so it takes the cached answer.
          onClick={() => load({ refresh: Boolean(data) })}
          disabled={loading}
          style={styles.button}
        >
          {loading
            ? t("satSnapshotLoading")
            : data
            ? t("satSnapshotRefresh")
            : t("satSnapshotFetch")}
        </button>
      </div>

      {!data && !loading && !error && (
        <p style={styles.hint}>{t("satSnapshotHint")}</p>
      )}

      {/* The basemap's own age, stated permanently rather than on demand (#82).
          It sits here because this panel exists for the contrast between the two
          images, and until now only one of them was dated: this snapshot carried
          its capture date, cloud cover and metres-per-pixel, while the sharper
          picture underneath it carried nothing. An operator comparing them saw
          one labelled image and one unlabelled one, and the unlabelled one looks
          more authoritative.

          Static, not fetched. Esri's metadata service does publish this per
          location, and querying it would keep the figure correct as the mosaic is
          refreshed — but that is an endpoint, a cache and a failure mode, and the
          date changes on Esri's release cadence rather than ours. The trade is
          deliberate and its cost is that THIS STRING WILL EVENTUALLY BE WRONG.
          Re-check it with the command in SATELLITE_IMAGERY.md §1 before trusting
          it in anything that matters. */}
      <p style={styles.hint}>{t("basemapVintage")}</p>

      {error && <p style={styles.error}>{t("satSnapshotError")}: {error}</p>}

      {cooldown != null && (
        <p style={styles.hint}>{t("satSnapshotCooldown").replace("{n}", cooldown)}</p>
      )}

      {data && <Result data={data} t={t} />}
    </div>
  );
}

function Result({ data, t }) {
  const {
    status,
    image_url,
    image_status,
    image_reason,
    capture_date,
    capture_age_days,
    cloud_cover_pct,
    metres_per_pixel,
    scene_id,
    source,
    attribution,
    newer_scenes_rejected,
    cloud_threshold_pct,
    search_days,
    reason,
  } = data;

  // Nothing was found and nothing was wrong: the sky was in the way. Kept
  // visually distinct from an error, because "we looked and could not see"
  // is an answer, not a fault.
  if (status === "no_scene")
    return (
      <div style={styles.stateBox}>
        <strong style={styles.stateTitle}>{t("satSnapshotNoScene")}</strong>
        <p style={styles.stateBody}>
          {reason ??
            t("satSnapshotNoSceneBody")
              .replace("{days}", search_days)
              .replace("{pct}", cloud_threshold_pct)}
        </p>
      </div>
    );

  // The check itself could not be made. Distinct from the above on purpose.
  if (status === "unavailable")
    return (
      <div style={styles.stateBox}>
        <strong style={styles.stateTitle}>{t("satSnapshotUnavailable")}</strong>
        <p style={styles.stateBody}>{reason ?? t("satSnapshotUnavailableBody")}</p>
      </div>
    );

  const cloudy = typeof cloud_cover_pct === "number" && cloud_cover_pct > CLOUD_WARN_PCT;

  return (
    <div>
      {/* Reaching back past cloudy passes is stated before the image, not
          under it: it changes what the picture means. */}
      {status === "older_scene" && (
        <div style={styles.olderBanner}>
          {t("satSnapshotOlder").replace("{n}", newer_scenes_rejected)}
        </div>
      )}

      {image_status === "ok" && image_url ? (
        <img
          src={image_url}
          alt={t("satSnapshotAlt")
            .replace("{date}", formatDate(capture_date))
            .replace("{id}", scene_id ?? "")}
          style={styles.image}
        />
      ) : (
        // The scene metadata is known even when the pixels are not — the
        // catalogue needs no credentials and the renderer does. Saying "a
        // cloud-free scene from N days ago exists but cannot be rendered" is
        // more use than a blank box.
        <div style={styles.stateBox}>
          <strong style={styles.stateTitle}>{t("satSnapshotNoImage")}</strong>
          <p style={styles.stateBody}>{image_reason ?? t("satSnapshotNoImageBody")}</p>
        </div>
      )}

      <dl style={styles.meta}>
        <MetaRow label={t("satSnapshotCaptured")}>
          {formatDate(capture_date)}
          {capture_age_days != null && (
            <span style={styles.dim}>
              {" "}
              ({capture_age_days === 0
                ? t("satSnapshotToday")
                : t("satSnapshotDaysAgo").replace("{n}", capture_age_days)})
            </span>
          )}
        </MetaRow>

        <MetaRow label={t("satSnapshotCloud")}>
          <span style={cloudy ? styles.cloudWarn : undefined}>
            {cloud_cover_pct == null ? t("satSnapshotUnknown") : `${cloud_cover_pct.toFixed(2)}%`}
          </span>
          <span style={styles.dim}> {t("satSnapshotCloudScope")}</span>
        </MetaRow>

        {/* Stated so nobody reads a 10 m raster as a photograph of a tower. */}
        <MetaRow label={t("satSnapshotResolution")}>
          {metres_per_pixel} m / {t("satSnapshotPixel")}
          <span style={styles.dim}> — {t("satSnapshotResolutionNote")}</span>
        </MetaRow>

        <MetaRow label={t("satSnapshotSource")}>
          {source}
          {scene_id && <div style={styles.sceneId}>{scene_id}</div>}
        </MetaRow>
      </dl>

      <p style={styles.attribution}>{attribution}</p>
    </div>
  );
}

function MetaRow({ label, children }) {
  return (
    <div style={styles.metaRow}>
      <dt style={styles.metaKey}>{label}</dt>
      <dd style={styles.metaValue}>{children}</dd>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

const styles = {
  wrap: { marginTop: 16, paddingTop: 12, borderTop: "1px solid #e5e7eb" },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 8,
  },
  title: { fontWeight: 600, fontSize: 15, display: "inline-flex", alignItems: "center", gap: 2 },
  button: {
    border: "1px solid #d1d5db",
    background: "#fff",
    borderRadius: 6,
    padding: "4px 10px",
    fontSize: 13,
    cursor: "pointer",
  },
  hint: { margin: 0, fontSize: 12, color: "#6b7280" },
  error: { margin: 0, fontSize: 13, color: "#b91c1c" },
  image: {
    width: "100%",
    maxWidth: 500,
    aspectRatio: "1 / 1",
    objectFit: "cover",
    display: "block",
    borderRadius: 6,
    border: "1px solid #e5e7eb",
    background: "#f3f4f6",
  },
  olderBanner: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    color: "#92400e",
    borderRadius: 6,
    padding: "6px 8px",
    fontSize: 12,
    marginBottom: 8,
  },
  stateBox: {
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: 6,
    padding: 10,
  },
  stateTitle: { fontSize: 13 },
  stateBody: { margin: "4px 0 0", fontSize: 12, color: "#6b7280" },
  meta: { margin: "10px 0 0" },
  metaRow: { display: "flex", gap: 8, fontSize: 12, padding: "3px 0" },
  metaKey: { margin: 0, color: "#6b7280", flex: "0 0 96px" },
  metaValue: { margin: 0, color: "#111827" },
  sceneId: { color: "#9ca3af", wordBreak: "break-all", fontSize: 11 },
  dim: { color: "#9ca3af" },
  cloudWarn: { color: "#92400e", fontWeight: 600 },
  attribution: { margin: "8px 0 0", fontSize: 11, color: "#9ca3af" },
};
